import { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { ChatCartComponentProps } from 'tripPlanner/components/Chat/chatCartTypes'
import ChatCartHistoricalNote from './ChatCartHistoricalNote'
import { useCartPanelContainerState } from '../../hooks/useCartPanelContainerState'
import type { CartPanelItem } from '../../hooks/useCartPanelItems'
import ChatCartRow from './ChatCartRow'
import ChatCartCategoryChip from './ChatCartCategoryChip'
import {
  CHAT_CART_CATEGORY,
  countChatCartBlockedItems,
  countChatCartBookableItems,
  filterChatCartItems,
  getChatCartCategoryOptions,
  type ChatCartCategory,
} from './chatCartItems'

const Card = styled.div`
  width: 100%;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const ChipRow = styled.div`
  display: flex;
  gap: ${rem(8)};
  overflow-x: auto;
  padding-block-end: ${rem(4)};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  border-block-start: 1px solid ${props => props.theme.palette.neutral.default.six};

  > li + li {
    border-block-start: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const Footer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${rem(12)};
  padding-block-start: ${rem(12)};
  border-block-start: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const FooterTotal = styled.div`
  min-width: 0;
`

const BlockerNote = styled(BodyText)`
  color: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
`

const CATEGORY_ICONS = {
  [CHAT_CART_CATEGORY.FLIGHT]: <LinePlaneDepartureIcon />,
  [CHAT_CART_CATEGORY.ACCOMMODATION]: <LineBedIcon />,
  [CHAT_CART_CATEGORY.EXPERIENCE]: <LineHotAirBalloonIcon />,
} as const

function ChatCartLive() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const eventBus = useTripPlannerEventBus()
  const [category, setCategory] = useState<ChatCartCategory>(CHAT_CART_CATEGORY.ALL)

  const {
    items,
    isLoading,
    totalPrice,
    hasCheckoutReadyItems,
    isLoggedIn,
    agentActionsDisabled,
    handleChooseTickets,
    handleChooseDifferentTickets,
    handleChooseDate,
    handleChooseTime,
    handleChooseFlight,
    handleRefreshFlight,
    handleChangeFlight,
    refreshingFlightKey,
    handleCheckout,
    isCheckingOut,
    flightSearchDrawer,
    flightDetailsDrawer,
  } = useCartPanelContainerState()

  const categoryOptions = useMemo(() => getChatCartCategoryOptions(items), [items])
  // The cart changes under the widget as items are booked, removed or synced. If the
  // selected category stops being offered, fall back to All rather than filtering the
  // list down to nothing with no chip left to show why.
  const activeCategory = categoryOptions.some(option => option.category === category)
    ? category
    : CHAT_CART_CATEGORY.ALL
  const visibleItems = useMemo(
    () => filterChatCartItems(items, activeCategory),
    [items, activeCategory],
  )
  const bookableCount = useMemo(() => countChatCartBookableItems(items), [items])
  const blockedCount = useMemo(() => countChatCartBlockedItems(items), [items])

  const handleOpenOffer = useCallback((item: CartPanelItem) => {
    if (item.productType !== 'accommodation' || !item.offerId) return
    // Carry the stay dates through like CartPanelActiveHotelTile does, so a hotel that
    // only needs a package doesn't land on an undated offer page asking the customer to
    // re-enter dates they already chose.
    const { checkIn, checkOut } = item
    eventBus.dispatch({
      type: 'open_offer_drawer',
      offerId: item.offerId,
      searchParams: checkIn || checkOut ? { checkIn, checkOut } : undefined,
      scrollToRoomOptions: true,
    })
  }, [eventBus])

  const handleChooseFlightForItem = useCallback((item: CartPanelItem) => {
    if (item.productType !== 'flight') return
    handleChooseFlight(item)
  }, [handleChooseFlight])

  const handleRefreshFlightForItem = useCallback((item: CartPanelItem) => {
    if (item.productType !== 'flight') return
    handleRefreshFlight(item)
  }, [handleRefreshFlight])

  const handleChangeFlightForItem = useCallback((item: CartPanelItem) => {
    if (item.productType !== 'flight') return
    handleChangeFlight(item)
  }, [handleChangeFlight])

  const handleCheckoutClick = useCallback(() => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    handleCheckout()
  }, [isLoggedIn, showModal, handleCheckout])

  const categoryLabels: Record<ChatCartCategory, string> = useMemo(() => ({
    [CHAT_CART_CATEGORY.ALL]: intl.formatMessage({
      id: 'SjtALC5AnkGvv1Hbs9OI',
      defaultMessage: 'All',
      description: 'Filter chip in the chat cart widget that shows every item in the trip planner cart',
    }),
    [CHAT_CART_CATEGORY.FLIGHT]: intl.formatMessage({
      id: 'ngcCNUBlYflpWDnKtLAl',
      defaultMessage: 'Flights',
      description: 'Section title for flight items in the trip planner cart panel',
    }),
    [CHAT_CART_CATEGORY.ACCOMMODATION]: intl.formatMessage({
      id: 'HfU7-K_LUalrV8r0P5Bz',
      defaultMessage: 'Hotels',
      description: 'Filter chip in the chat cart widget that narrows the cart to accommodation items only',
    }),
    [CHAT_CART_CATEGORY.EXPERIENCE]: intl.formatMessage({
      id: 'ex4Hhv75OUDizjt1F064',
      defaultMessage: 'Experiences',
      description: 'Section title for experience items in the trip planner cart panel',
    }),
  }), [intl])

  const formatChipLabel = useCallback((chipCategory: ChatCartCategory, count: number) => intl.formatMessage(
    {
      id: '7lo_Naoljm01OCef5YZL',
      defaultMessage: '{label} {count}',
      description: 'Filter chip label in the chat cart widget pairing a product category name with how many of those items are in the cart',
    },
    { label: categoryLabels[chipCategory], count },
  ), [intl, categoryLabels])

  const checkoutLabel = useMemo(() => {
    if (!isLoggedIn) {
      return intl.formatMessage({
        id: 'PsWbPJA5WZimhO6Dv0Zl',
        defaultMessage: 'Log in to checkout',
        description: 'Cart panel checkout button text when user is not logged in',
      })
    }

    if (blockedCount > 0 && bookableCount > 0) {
      return intl.formatMessage(
        {
          id: 'zcfBNDYkc5JEa73UdoK7',
          defaultMessage: 'Book {bookable} of {total}',
          description: 'Checkout button in the chat cart widget when only some items are ready, showing how many will be booked',
        },
        // Deselected items are neither bookable nor blocked, so counting them in the
        // denominator would quote a total the customer never asked to book.
        { bookable: bookableCount, total: bookableCount + blockedCount },
      )
    }

    return intl.formatMessage({
      id: '7Ej3duOhiVx-nzIcPorw',
      defaultMessage: 'Book now',
      description: 'Primary checkout button text shown in the trip planner cart panel footer',
    })
  }, [intl, isLoggedIn, blockedCount, bookableCount])

  const subtotalLabel = intl.formatMessage({
    id: 'DJmbrI4j_n5HY_aDFZq_',
    defaultMessage: 'Subtotal',
    description: 'Label above the running total in the chat cart widget, before taxes and fees are finalised',
  })

  const blockerNote = blockedCount > 0
    ? intl.formatMessage(
        {
          id: '72DOtW_TiEpooT_ZgfDH',
          defaultMessage: '{count, plural, one {# item needs details before it can be booked} other {# items need details before they can be booked}}',
          description: 'Cart footer message below checkout explaining how many trip items need more details before booking',
        },
        { count: blockedCount },
      )
    : undefined

  // The model can ask for the cart before anything is in it, and it never sees the
  // live cart when the message renders. Showing nothing is better than an empty box.
  if (!isLoading && items.length === 0) return null

  return (
    <Card data-testid="chat-cart">
      <Group direction="vertical" gap={12}>
        <Heading variant="heading6" as="h3">
          {intl.formatMessage({
            id: 'KTfuxaACrTyQp8QTN40V',
            defaultMessage: 'Your cart',
            description: 'Heading of the cart widget shown inline in trip planner chat above the bookable trip items',
          })}
        </Heading>

        {categoryOptions.length > 0 && (
          <ChipRow>
            {categoryOptions.map(option => (
              <ChatCartCategoryChip
                key={option.category}
                category={option.category}
                label={formatChipLabel(option.category, option.count)}
                icon={CATEGORY_ICONS[option.category as keyof typeof CATEGORY_ICONS]}
                isSelected={activeCategory === option.category}
                onSelect={setCategory}
              />
            ))}
          </ChipRow>
        )}

        {isLoading && items.length === 0 && (
          <Group direction="horizontal" horizontalAlign="center">
            <LoadingSpinner size={20} />
          </Group>
        )}

        {visibleItems.length > 0 && (
          <ItemList>
            {visibleItems.map(item => (
              <ChatCartRow
                key={item.key}
                item={item}
                isDisabled={agentActionsDisabled}
                isRefreshing={item.key === refreshingFlightKey}
                onOpenOffer={handleOpenOffer}
                onChooseTickets={handleChooseTickets}
                onChooseDifferentTickets={handleChooseDifferentTickets}
                onChooseDate={handleChooseDate}
                onChooseTime={handleChooseTime}
                onChooseFlight={handleChooseFlightForItem}
                onRefreshFlight={handleRefreshFlightForItem}
                onChangeFlight={handleChangeFlightForItem}
              />
            ))}
          </ItemList>
        )}

        {items.length > 0 && (
          <Footer>
            <FooterTotal>
              <BodyText variant="small" colour="neutral-three">{subtotalLabel}</BodyText>
              {isLoading && <TextLoadingBox typography="heading6" width="8ch" />}
              {!isLoading && (
                <PriceRowPrice price={totalPrice} priceFormat="preciseDollar" saleUnit="" size="M" />
              )}
              {blockerNote && <BlockerNote variant="small" weight="bold">{blockerNote}</BlockerNote>}
            </FooterTotal>
            <TextButton
              kind="primary"
              size="medium"
              disabled={isLoggedIn && (isLoading || isCheckingOut || !hasCheckoutReadyItems)}
              aria-busy={isCheckingOut}
              startIcon={isCheckingOut ? <LoadingSpinner inline size={16} /> : undefined}
              onClick={handleCheckoutClick}
            >
              {checkoutLabel}
            </TextButton>
          </Footer>
        )}
      </Group>
      {flightSearchDrawer}
      {flightDetailsDrawer}
    </Card>
  )
}

/**
 * Only the newest cart widget reads live cart state. Older ones collapse to a static
 * line — otherwise every cart the assistant ever emitted would mount its own copy of the
 * cart state and show competing totals and book buttons for the same cart.
 */
function ChatCart({ isLastMessage }: ChatCartComponentProps) {
  if (!isLastMessage) return <ChatCartHistoricalNote />

  return <ChatCartLive />
}

export default ChatCart
